/**
 * The FEP — Front-End Processor (ADR-080). The SOW trust-boundary tier.
 *
 * ⚑ §9 COPY-FORK. Canonical source: portal-subscriber/src/obw/fep.ts. This is an
 * INDEPENDENT copy for the portal-staff auth mount (Structure Guide §9 Common
 * Catalogue Model) — NOT a shared-runtime import. A fix here is applied to the
 * other copy deliberately, one surface at a time; the two never share a module.
 * Per §9.3, this second use is what promoted the FEP into the common catalogue.
 *
 * "FEP" here is the MAINFRAME sense (the IBM 3705/3725 comms controller that sat
 * IN FRONT OF the mainframe, terminated every incoming line, absorbed the hostile
 * outside world, and passed only clean, framed, validated traffic inward). It is
 * NOT the modern web "front-end processor" (a presentation-layer input massager).
 * The FEP terminates BOTH SOW mounts, establishes/validates trust per mount, and
 * hands the journey a single VALIDATED onboarding context. The core (provision
 * routes, DDB, Cognito) never processes an untrusted request — only a context the
 * FEP has already vouched for (ADR-080 §3.1).
 *
 * ONE forkable component, TWO code paths — it assesses its own mount context at
 * entry and branches (ADR-080 §3.2):
 *   - unauth (public website)  → trust established BY PAYMENT (card-first, ADR-073);
 *   - auth   (nexa-admin)      → trust established BY the admin HMAC LAUNCH TOKEN
 *                                (the POW pattern, ADR-076 Am.1).
 * The FEP carries NO ambient trust of its own: the MOUNT supplies trust, the FEP
 * VERIFIES it per context (§3.2). Context-detection inside a forkable component is
 * NOT a shared-runtime module — it is the ADR-077 assess-at-entry principle applied
 * at the trust tier, and this file is copy-forked per surface (Structure Guide §9),
 * never imported across surfaces.
 *
 * §9-clean fork note: the canonical copy lives here (portal-subscriber, alongside
 * assess.ts); portal-staff carries an independent copy-fork of the SAME logic for
 * the auth mount. Two copies, decoupled — a fix to one is applied to the other
 * deliberately, one surface at a time.
 *
 * DISTINCT from ADR-077 `assess.ts` (ADR-080 §3.4): the FEP's resolved-context
 * output is its OWN SOW-specific type ({@link SowFepContext}) — deliberately NOT a
 * subtype of, or a dependency on, assess.ts's resolution type. Related in spirit
 * (both assess-at-entry and resolve into a uniform downstream shape), never
 * conflated: "FEP context" must always mean exactly one thing in the doc set.
 * This module therefore imports NOTHING from assess.ts.
 *
 * THE GOVERNING PRINCIPLE (locked, ADR-080 §3.3): the journey is MOUNT-BLIND; the
 * context is not. No downstream SOW step branches on mount origin. All
 * mount-specific truth is carried as DATA in the normalised context — never as
 * step-level branching. "Context is everything."
 */

/**
 * How the FEP ESTABLISHED trust for this SOW entry (ADR-080 §2.3). Trust is
 * supplied by the mount and verified by the FEP per path — never ambient.
 *   - `payment`      — unauth public: the card-first payment seam is the trust
 *                      boundary (no account-bearing side effect until the charge
 *                      confirms, ADR-073 §2).
 *   - `launch-token` — auth nexa-admin: an admin-minted HMAC launch token carries
 *                      the mount's trust; it is re-verified server-side at the
 *                      provision route (ADR-076 Am.1). The `token` is opaque here.
 */
export type SowTrust =
  | { via: 'payment' }
  | { via: 'launch-token'; token: string };

/**
 * How the completion step PROVISIONS — the context carries "how to provision"
 * (ADR-080 §3.3) so the completion step executes it WITHOUT knowing the origin.
 *   - `payment-confirm` — unauth: provision only once the (stubbed, ADR-073 Am.1)
 *                         payment result confirms.
 *   - `launch-token`    — auth: provision against the validated admin launch token.
 */
export type SowProvisioning =
  | { trigger: 'payment-confirm' }
  | { trigger: 'launch-token'; token: string };

/**
 * The FEP's resolved onboarding context — the SINGLE shape BOTH mounts normalise
 * into, and the ONLY thing the mount-blind SOW journey consumes. DISTINCT from
 * ADR-077's resolution type (§3.4). Steps read these fields; they never read
 * "which mount am I?".
 */
export interface SowFepContext {
  /** How trust was established for this entry (see {@link SowTrust}). */
  trust: SowTrust;
  /**
   * The launching Nexa consultant's display name (auth path) or `null` (unauth
   * path — no staff member). The `profile` step renders the "Nexa Consultant"
   * field IFF this is present; it never asks which mount it is on (ADR-080 §3.3).
   */
  nexaConsultant: string | null;
  /** How the completion step provisions (see {@link SowProvisioning}). */
  provisioning: SowProvisioning;
}

/**
 * The raw mount signals the FEP assesses at entry. The MOUNT supplies these; the
 * FEP verifies + normalises them into a {@link SowFepContext}. The PRESENCE of a
 * launch token IS the auth path; its absence IS the public path — the mount
 * origin is inferred from the signals, never passed as an explicit "which mount"
 * flag (so no step can ever branch on it).
 */
export interface SowMountSignals {
  /**
   * The admin HMAC SOW launch token (from `/admin/sow/launch`, delivered as the
   * staff-mount seed or a `?token=` param). Absent/empty on the public mount.
   */
  launchToken?: string | null;
  /**
   * The launching consultant's display name — only meaningful on the auth path
   * (the staff session supplies it). Ignored on the public path.
   */
  nexaConsultant?: string | null;
}

/**
 * assessSowMount — the FEP entry assessment (ADR-080 §3.2). ONE function, TWO
 * code paths, branching on the assessed mount context; NORMALISES both mounts
 * into the SAME {@link SowFepContext} shape so the journey is mount-blind.
 *
 * Perimeter note: this pure resolution is the FEP's trust-tier core. The
 * deterministic perimeter controls on the public mount (rate-limit + countdown
 * modal, capture-everything) are the FEP's defensive behaviour on the unauth
 * path and live alongside this at the public mount — they are not part of the
 * pure normalisation and so are kept out of this side-effect-free function.
 */
export function assessSowMount(signals: SowMountSignals): SowFepContext {
  const token = (signals.launchToken ?? '').trim();

  if (token) {
    // AUTH path — an admin launch token is present. Trust IS the token (the FEP
    // holds no ambient trust; the token is re-verified server-side at provision).
    // nexaConsultant is carried as DATA from the staff session.
    const consultant = (signals.nexaConsultant ?? '').trim();
    return {
      trust: { via: 'launch-token', token },
      nexaConsultant: consultant || null,
      provisioning: { trigger: 'launch-token', token },
    };
  }

  // UNAUTH path — public card-first. No token; trust is established BY PAYMENT.
  // The FEP NORMALISES: nexaConsultant is forced `null` (the public mount has no
  // staff member) even if a caller passed one — so both mounts yield the same
  // shape and the journey stays mount-blind.
  return {
    trust: { via: 'payment' },
    nexaConsultant: null,
    provisioning: { trigger: 'payment-confirm' },
  };
}

/** True iff the FEP context was resolved on the authenticated (nexa-admin) mount.
 *  For MOUNT-LOGIC use only (e.g. the mount seeding the journey or the perimeter
 *  deciding whether to arm public-only controls) — NEVER for step-level branching
 *  (steps read {@link SowFepContext.nexaConsultant} / `.provisioning`). Derived
 *  from the trust discriminant, so there is no separate "which mount" field. */
export function isAuthMount(ctx: SowFepContext): boolean {
  return ctx.trust.via === 'launch-token';
}
